import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, unref as _unref, renderList as _renderList, Fragment as _Fragment, vModelCheckbox as _vModelCheckbox, withModifiers as _withModifiers, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "request-form-view" }
const _hoisted_2 = { class: "form-container" }
const _hoisted_3 = {
  key: 0,
  class: "success-message"
}
const _hoisted_4 = {
  key: 1,
  class: "error-message"
}
const _hoisted_5 = { class: "form-section" }
const _hoisted_6 = { class: "form-row" }
const _hoisted_7 = { class: "form-group" }
const _hoisted_8 = { class: "form-group" }
const _hoisted_9 = { class: "form-row" }
const _hoisted_10 = { class: "form-group" }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "form-group" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = { value: "" }
const _hoisted_15 = ["value"]
const _hoisted_16 = { class: "form-group" }
const _hoisted_17 = { class: "form-section" }
const _hoisted_18 = { class: "form-row" }
const _hoisted_19 = { class: "form-group" }
const _hoisted_20 = { class: "form-group" }
const _hoisted_21 = { class: "form-section" }
const _hoisted_22 = {
  key: 0,
  class: "loading"
}
const _hoisted_23 = { key: 1 }
const _hoisted_24 = { class: "task-checkboxes" }
const _hoisted_25 = ["value"]
const _hoisted_26 = { class: "task-info" }
const _hoisted_27 = { class: "task-label" }
const _hoisted_28 = { class: "task-description" }
const _hoisted_29 = { class: "form-section" }
const _hoisted_30 = { class: "form-group" }
const _hoisted_31 = { class: "form-actions" }
const _hoisted_32 = ["disabled"]

import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useSchools } from '@/composables/useSchools'

interface Task {
  id: string
  label: string
  description: string
}

interface TaskGroups {
  [category: string]: Task[]
}

interface Teacher {
  id: string
  first_name: string
  last_name: string
  email: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RequestFormView',
  setup(__props) {

/* eslint-disable no-undef */
const { schools, loadSchools } = useSchools()

const formData = ref({
  student_name: '',
  grade_level: '',
  school: '',
  teacher_email: '',
  technology_access: '',
  contact_email: '',
  contact_phone: '',
  equipment_type: 'laptop',
  needed_for: [] as string[],
  needed_software: [] as string[],
  additional_comments: ''
})

const teachers = ref<Teacher[]>([])
const selectedTeacher = ref('')
const loadingTeachers = ref(false)
const showEmailInput = ref(true)
const softwareText = ref('')
const loadingTasks = ref(true)
const taskGroups = ref<TaskGroups>({})
const submitting = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

// Parse software text into array
const parsedSoftware = computed(() => {
  if (!softwareText.value.trim()) return []

  // Split by newlines or commas
  return softwareText.value
    .split(/[\n,]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0)
})

// Load available tasks from API
const loadTasks = async () => {
  try {
    const response = await axios.get('/api/v1/requests/tasks/available')
    taskGroups.value = response.data.groups
  } catch (error) {
    console.error('Error loading tasks:', error)
    errorMessage.value = 'Failed to load task options. Please refresh the page.'
  } finally {
    loadingTasks.value = false
  }
}

// Load teachers for selected school
const loadTeachers = async (schoolId: string) => {
  if (!schoolId) {
    teachers.value = []
    return
  }

  loadingTeachers.value = true
  try {
    const response = await axios.get(`/api/v1/schools/${schoolId}/teachers`)
    teachers.value = response.data
  } catch (error) {
    console.error('Error loading teachers:', error)
    teachers.value = []
  } finally {
    loadingTeachers.value = false
  }
}

// Handle school change
const onSchoolChange = () => {
  selectedTeacher.value = ''
  formData.value.teacher_email = ''
  showEmailInput.value = true
  if (formData.value.school) {
    loadTeachers(formData.value.school)
  } else {
    teachers.value = []
  }
}

// Handle teacher selection
const onTeacherSelect = () => {
  if (selectedTeacher.value && selectedTeacher.value !== 'other') {
    // Find the selected teacher and set their email
    const teacher = teachers.value.find(t => t.id === selectedTeacher.value)
    if (teacher) {
      formData.value.teacher_email = teacher.email
      showEmailInput.value = false
    }
  } else if (selectedTeacher.value === 'other') {
    // Show email input and clear the email field
    formData.value.teacher_email = ''
    showEmailInput.value = true
  } else {
    // No teacher selected
    formData.value.teacher_email = ''
    showEmailInput.value = true
  }
}

const submitRequest = async () => {
  if (formData.value.needed_for.length === 0) {
    errorMessage.value = 'Please select at least one task the student needs the equipment for.'
    return
  }

  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    // Parse software list
    formData.value.needed_software = parsedSoftware.value

    const response = await axios.post('/api/v1/requests', formData.value)

    successMessage.value = 'Request submitted successfully! We will review your request and contact you soon.'

    // Reset form
    formData.value = {
      student_name: '',
      grade_level: '',
      school: '',
      teacher_email: '',
      technology_access: '',
      contact_email: '',
      contact_phone: '',
      equipment_type: 'laptop',
      needed_for: [],
      needed_software: [],
      additional_comments: ''
    }
    softwareText.value = ''

    // Scroll to top to show success message
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (error: any) {
    console.error('Error submitting request:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to submit request. Please try again.'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  loadTasks()
  loadSchools()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[26] || (_cache[26] = _createElementVNode("div", { class: "form-header" }, [
        _createElementVNode("h1", null, "Request Equipment"),
        _createElementVNode("p", null, "Fill out this form to request computer equipment for your student")
      ], -1)),
      (successMessage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(successMessage.value), 1))
        : _createCommentVNode("", true),
      (errorMessage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(errorMessage.value), 1))
        : _createCommentVNode("", true),
      (!successMessage.value)
        ? (_openBlock(), _createElementBlock("form", {
            key: 2,
            onSubmit: _withModifiers(submitRequest, ["prevent"]),
            class: "request-form"
          }, [
            _createElementVNode("section", _hoisted_5, [
              _cache[18] || (_cache[18] = _createElementVNode("h2", null, "Student Information", -1)),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _cache[10] || (_cache[10] = _createElementVNode("label", { for: "student_name" }, "Child's Name *", -1)),
                  _withDirectives(_createElementVNode("input", {
                    id: "student_name",
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.student_name) = $event)),
                    type: "text",
                    required: "",
                    placeholder: "Enter student's full name"
                  }, null, 512), [
                    [_vModelText, formData.value.student_name]
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _cache[12] || (_cache[12] = _createElementVNode("label", { for: "grade_level" }, "Grade Level *", -1)),
                  _withDirectives(_createElementVNode("select", {
                    id: "grade_level",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.grade_level) = $event)),
                    required: ""
                  }, [...(_cache[11] || (_cache[11] = [
                    _createStaticVNode("<option value=\"\" data-v-6c9fb22c>Select grade level...</option><option value=\"Pre-K\" data-v-6c9fb22c>Pre-K</option><option value=\"K\" data-v-6c9fb22c>K</option><option value=\"1st\" data-v-6c9fb22c>1st</option><option value=\"2nd\" data-v-6c9fb22c>2nd</option><option value=\"3rd\" data-v-6c9fb22c>3rd</option><option value=\"4th\" data-v-6c9fb22c>4th</option><option value=\"5th\" data-v-6c9fb22c>5th</option><option value=\"6th\" data-v-6c9fb22c>6th</option><option value=\"7th\" data-v-6c9fb22c>7th</option><option value=\"8th\" data-v-6c9fb22c>8th</option><option value=\"9th\" data-v-6c9fb22c>9th</option><option value=\"10th\" data-v-6c9fb22c>10th</option><option value=\"11th\" data-v-6c9fb22c>11th</option><option value=\"12th\" data-v-6c9fb22c>12th</option>", 15)
                  ]))], 512), [
                    [_vModelSelect, formData.value.grade_level]
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _cache[14] || (_cache[14] = _createElementVNode("label", { for: "school" }, "School *", -1)),
                  _withDirectives(_createElementVNode("select", {
                    id: "school",
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.school) = $event)),
                    required: "",
                    onChange: onSchoolChange
                  }, [
                    _cache[13] || (_cache[13] = _createElementVNode("option", { value: "" }, "-- Select School --", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(schools), (school) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: school.id,
                        value: school.id
                      }, _toDisplayString(school.name), 9, _hoisted_11))
                    }), 128))
                  ], 544), [
                    [_vModelSelect, formData.value.school]
                  ])
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _cache[16] || (_cache[16] = _createElementVNode("label", { for: "teacher_select" }, "Teacher", -1)),
                  _withDirectives(_createElementVNode("select", {
                    id: "teacher_select",
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedTeacher).value = $event)),
                    disabled: !formData.value.school || loadingTeachers.value,
                    onChange: onTeacherSelect
                  }, [
                    _createElementVNode("option", _hoisted_14, _toDisplayString(loadingTeachers.value ? 'Loading teachers...' : '-- Select Teacher or Enter Email Below --'), 1),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(teachers.value, (teacher) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: teacher.id,
                        value: teacher.id
                      }, _toDisplayString(teacher.first_name.charAt(0)) + ". " + _toDisplayString(teacher.last_name), 9, _hoisted_15))
                    }), 128)),
                    _cache[15] || (_cache[15] = _createElementVNode("option", { value: "other" }, "Other (enter email below)", -1))
                  ], 40, _hoisted_13), [
                    [_vModelSelect, selectedTeacher.value]
                  ]),
                  (showEmailInput.value)
                    ? _withDirectives((_openBlock(), _createElementBlock("input", {
                        key: 0,
                        id: "teacher_email",
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.value.teacher_email) = $event)),
                        type: "email",
                        placeholder: "teacher@school.edu",
                        class: "teacher-email-input"
                      }, null, 512)), [
                        [_vModelText, formData.value.teacher_email]
                      ])
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("div", _hoisted_16, [
                _cache[17] || (_cache[17] = _createElementVNode("label", { for: "technology_access" }, "Current Access to Technology at Home *", -1)),
                _withDirectives(_createElementVNode("textarea", {
                  id: "technology_access",
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.technology_access) = $event)),
                  rows: "3",
                  required: "",
                  placeholder: "e.g., Shared family computer, tablet, smartphone only..."
                }, null, 512), [
                  [_vModelText, formData.value.technology_access]
                ])
              ])
            ]),
            _createElementVNode("section", _hoisted_17, [
              _cache[21] || (_cache[21] = _createElementVNode("h2", null, "Contact Information", -1)),
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("div", _hoisted_19, [
                  _cache[19] || (_cache[19] = _createElementVNode("label", { for: "contact_email" }, "Email *", -1)),
                  _withDirectives(_createElementVNode("input", {
                    id: "contact_email",
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.value.contact_email) = $event)),
                    type: "email",
                    required: "",
                    placeholder: "your-email@example.com"
                  }, null, 512), [
                    [_vModelText, formData.value.contact_email]
                  ])
                ]),
                _createElementVNode("div", _hoisted_20, [
                  _cache[20] || (_cache[20] = _createElementVNode("label", { for: "contact_phone" }, "Phone", -1)),
                  _withDirectives(_createElementVNode("input", {
                    id: "contact_phone",
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formData.value.contact_phone) = $event)),
                    type: "tel",
                    placeholder: "(321) 555-1234"
                  }, null, 512), [
                    [_vModelText, formData.value.contact_phone]
                  ])
                ])
              ])
            ]),
            _createElementVNode("section", _hoisted_21, [
              _cache[22] || (_cache[22] = _createElementVNode("h2", null, "What will the student use this equipment for? *", -1)),
              _cache[23] || (_cache[23] = _createElementVNode("p", { class: "section-description" }, "Select all tasks that apply", -1)),
              (loadingTasks.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_22, "Loading tasks..."))
                : (_openBlock(), _createElementBlock("div", _hoisted_23, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(taskGroups.value, (tasks, category) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: category,
                        class: "task-category"
                      }, [
                        _createElementVNode("h3", null, _toDisplayString(category), 1),
                        _createElementVNode("div", _hoisted_24, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tasks, (task) => {
                            return (_openBlock(), _createElementBlock("label", {
                              key: task.id,
                              class: "checkbox-label"
                            }, [
                              _withDirectives(_createElementVNode("input", {
                                type: "checkbox",
                                value: task.id,
                                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formData.value.needed_for) = $event))
                              }, null, 8, _hoisted_25), [
                                [_vModelCheckbox, formData.value.needed_for]
                              ]),
                              _createElementVNode("div", _hoisted_26, [
                                _createElementVNode("span", _hoisted_27, _toDisplayString(task.label), 1),
                                _createElementVNode("span", _hoisted_28, _toDisplayString(task.description), 1)
                              ])
                            ]))
                          }), 128))
                        ])
                      ]))
                    }), 128))
                  ]))
            ]),
            _createElementVNode("section", _hoisted_29, [
              _cache[25] || (_cache[25] = _createElementVNode("h2", null, "Additional Information", -1)),
              _createElementVNode("div", _hoisted_30, [
                _cache[24] || (_cache[24] = _createElementVNode("label", { for: "additional_comments" }, "Additional Comments", -1)),
                _withDirectives(_createElementVNode("textarea", {
                  id: "additional_comments",
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((formData.value.additional_comments) = $event)),
                  rows: "4",
                  placeholder: "Any additional information you'd like us to know..."
                }, null, 512), [
                  [_vModelText, formData.value.additional_comments]
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_31, [
              _createElementVNode("button", {
                type: "submit",
                disabled: submitting.value || formData.value.needed_for.length === 0,
                class: "btn-submit"
              }, _toDisplayString(submitting.value ? 'Submitting...' : 'Submit Request'), 9, _hoisted_32)
            ])
          ], 32))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})